import { Type } from 'class-transformer';
import {
	ArrayMaxSize,
	IsArray,
	IsBoolean,
	IsInt,
	IsOptional,
	IsString,
	Max,
	MaxLength,
	Min,
	ValidateNested,
} from 'class-validator';
import type { ApolloSpendResult } from '../../constants/apollo';
import type { ApolloRevealEntity } from '../entity/apollo-reveal.entity';

/**
 * PD-17611 — Apollo connection, limits, the credit report and the calls
 * every later feature makes through ApolloClientService.
 */

/** Connect (or reconnect) Apollo. On reconnect a blank key keeps the stored one. */
export class ApolloConnectRequestDto {
	@IsOptional()
	@IsString()
	@MaxLength(200)
	public apiKey?: string;
}

export interface ApolloConnectionStatusDto {
	connected: boolean;
	connectedDate?: string;
	connectedByUserName?: string;
}

/**
/** PD-17611 — the Apollo account's credits as Apollo reports them (free to read). */
export interface ApolloAccountCreditsDto {
	creditsRemaining: number;
	/** The plan's monthly credits; null when Apollo does not say. */
	monthlyCredits: number | null;
}

/**
 * What "Test" checks, all free: the key is accepted, the Synced and
 * Blocklist labels exist in the Apollo account (searches leave those people
 * out), how many people-search requests Apollo still allows today, and the
 * account's credits (null when Apollo does not report them).
 */
export interface ApolloTestResultDto {
	ok: boolean;
	syncedLabelFound: boolean;
	blocklistLabelFound: boolean;
	searchRequestsLeftToday: number | null;
	accountCredits: ApolloAccountCreditsDto | null;
}

export class ApolloRoleLimitDto {
	@IsInt()
	@Min(1)
	public roleId!: number;

	@IsInt()
	@Min(0)
	@Max(1_000_000)
	public monthlyCredits!: number;
}

/**
 * The limits and switches on the Apollo settings page, stored in the
 * `apollo.settings` instance setting. A null limit means no limit.
 */
export class ApolloSettingsDto {
	/** Workd's monthly budget: credits the whole tenant may spend in a month. */
	@IsOptional()
	@IsInt()
	@Min(0)
	@Max(10_000_000)
	public monthlyBudget!: number | null;

	/** One action above this many credits needs an admin. */
	@IsOptional()
	@IsInt()
	@Min(0)
	@Max(10_000_000)
	public approvalThreshold!: number | null;

	/** Monthly credits per person holding the role. */
	@IsArray()
	@ArrayMaxSize(200)
	@ValidateNested({ each: true })
	@Type(() => ApolloRoleLimitDto)
	public roleLimits!: Array<ApolloRoleLimitDto>;

	/** Fill in company background when an account is added with a website (one credit each). */
	@IsBoolean()
	public autoCompanyBackground!: boolean;

	/** When an email bounces, look for the person's new email or replacement (free search first). */
	@IsBoolean()
	public bounceFollowUp!: boolean;
}

export interface ApolloMonthSummaryDto {
	/** `YYYY-MM` in the tenant's zone. */
	periodKey: string;
	creditsUsed: number;
	searches: number;
	/** People returned by free searches. */
	peopleSearched: number;
	/** Results Workd already had, found without paying. */
	alreadyOurs: number;
	revealedPeople: number;
	revealCredits: number;
	companyLookups: number;
	companyCredits: number;
	/** Revealed people who were in Workd already. */
	misses: number;
	/** misses / revealedPeople as a 0-1 fraction; null before the first reveal. */
	missRate: number | null;
}

export interface ApolloUserSpendDto {
	/** Tenant user id; null for Workd's own jobs. */
	userId: number | null;
	userName: string | null;
	searches: number;
	peopleFound: number;
	reveals: number;
	companyLookups: number;
	credits: number;
}

export interface ApolloRoleOptionDto {
	id: number;
	name: string;
}

export interface ApolloTaggingSummaryDto {
	pending: number;
	tagged: number;
	failed: number;
}

export interface ApolloOverviewDto {
	connection: ApolloConnectionStatusDto;
	/** The Apollo account's credits as Apollo reports them; null when unavailable. */
	accountCredits: ApolloAccountCreditsDto | null;
	month: ApolloMonthSummaryDto;
	users: Array<ApolloUserSpendDto>;
	settings: ApolloSettingsDto;
	roles: Array<ApolloRoleOptionDto>;
	tagging: ApolloTaggingSummaryDto;
}

/**
 * A free people search. Workd always adds the verified-email filter and
 * leaves out everyone labeled Synced or Blocklist in the Apollo account.
 */
export class ApolloPeopleSearchRequestDto {
	@IsOptional()
	@IsArray()
	@ArrayMaxSize(50)
	@IsString({ each: true })
	public personTitles?: Array<string>;

	@IsOptional()
	@IsArray()
	@ArrayMaxSize(50)
	@IsString({ each: true })
	public personLocations?: Array<string>;

	@IsOptional()
	@IsArray()
	@ArrayMaxSize(100)
	@IsString({ each: true })
	public organizationIds?: Array<string>;

	@IsOptional()
	@IsArray()
	@ArrayMaxSize(100)
	@IsString({ each: true })
	public organizationDomains?: Array<string>;

	/** Apollo employee ranges such as "11,50". */
	@IsOptional()
	@IsArray()
	@ArrayMaxSize(20)
	@IsString({ each: true })
	public employeeRanges?: Array<string>;

	@IsOptional()
	@IsString()
	@MaxLength(200)
	public keywords?: string;

	/** PD-17612 — the kind of company, as Apollo company keywords ("distributor", "fasteners"). */
	@IsOptional()
	@IsArray()
	@ArrayMaxSize(20)
	@IsString({ each: true })
	public organizationKeywordTags?: Array<string>;

	@IsOptional()
	@IsInt()
	@Min(1)
	@Max(500)
	public page?: number;

	@IsOptional()
	@IsInt()
	@Min(1)
	@Max(100)
	public perPage?: number;

	/** The Workd feature asking, for the credit log. */
	@IsOptional()
	@IsString()
	@MaxLength(48)
	public feature?: string;
}

/** One free-search result: Apollo hides the last name and the email until a reveal. */
export interface ApolloPersonPreviewDto {
	apolloPersonId: string;
	firstName: string | null;
	lastNameHidden: string | null;
	title: string | null;
	organizationName: string | null;
	hasEmail: boolean;
	hasDirectPhone: boolean;
	/** PD-17612 — paid for before (the reveal log); adding them again is free. */
	revealedBefore?: boolean;
	/**
	 * PD-17612 — the Workd person already carrying this Apollo id, kept (not
	 * dropped) when the search asks for `keepInWorkd`: Find people offers them
	 * free when they are not on the account it is searching for.
	 */
	inWorkdContactId?: number;
}

export interface ApolloPeopleSearchResultDto {
	totalEntries: number;
	people: Array<ApolloPersonPreviewDto>;
	/** Results dropped before showing them, without paying. */
	leftOut: {
		inWorkd: number;
		revealedBefore: number;
	};
}

export class ApolloRevealRequestDto {
	@IsOptional()
	@IsString()
	@MaxLength(48)
	public feature?: string;

	/**
	 * PD-17612 — the free search result's first name and company, so the
	 * person can be looked for in our own Apollo contacts (free) before a
	 * credit is spent. Without them that free check cannot run.
	 */
	@IsOptional()
	@IsString()
	@MaxLength(100)
	public firstName?: string;

	@IsOptional()
	@IsString()
	@MaxLength(255)
	public organizationName?: string;
}

export type ApolloRevealedPersonDto = Pick<
	ApolloRevealEntity,
	| 'apolloPersonId'
	| 'firstName'
	| 'lastName'
	| 'title'
	| 'email'
	| 'emailStatus'
	| 'linkedinUrl'
	| 'phone'
	| 'apolloOrgId'
	| 'organizationName'
	| 'organizationDomain'
>;

export interface ApolloRevealResultDto {
	/** Null when Apollo found nobody, or Workd already had the person. */
	person: ApolloRevealedPersonDto | null;
	creditsSpent: number;
	/** Answered from the reveal log: paid for before, not paid again. */
	alreadyRevealed: boolean;
	/** The person is in Workd (found before paying, or a miss after). */
	inWorkd: boolean;
	contactId: number | null;
	result: ApolloSpendResult;
	/** PD-17612 — answered free from the people already in our Apollo account (no credit spent). */
	fromOurApollo?: boolean;
}

export class ApolloCompanyLookupRequestDto {
	@IsString()
	@MaxLength(253)
	public domain!: string;

	@IsOptional()
	@IsString()
	@MaxLength(48)
	public feature?: string;
}

export interface ApolloCompanyDto {
	apolloOrgId: string | null;
	name: string | null;
	domain: string | null;
	website: string | null;
	industry: string | null;
	employeeCount: number | null;
	annualRevenue: number | null;
	phone: string | null;
	street: string | null;
	city: string | null;
	state: string | null;
	postalCode: string | null;
	country: string | null;
	description: string | null;
	linkedinUrl: string | null;
}

export interface ApolloCompanyLookupResultDto {
	company: ApolloCompanyDto | null;
	creditsSpent: number;
	/** Answered from the credit log: looked up before, not paid again. */
	fromLog: boolean;
	result: ApolloSpendResult;
}

/**
 * The body of the 422 a limit answers with. `message` is written for the
 * person who was stopped.
 */
export interface ApolloLimitErrorDto {
	code: 'apollo_limit';
	refusal: 'budget' | 'role_limit' | 'approval';
	message: string;
	requestedCredits: number;
	limit: number;
	used: number;
}

